// "View as" (impersonation), ported from swiftMarketing (connectGS3.0 src/lib/impersonation.js + stores/auth.js), the
// same way swiftApp has it (SwiftProcessingSPA src/helpers/impersonation.js).
//
// The server decides everything: who the signed-in user may view as (GSDesignImpersonation/Targets, the same reach
// rules as swiftMarketing), and it re-checks X-GSD-Act-As on every GSWebAPIv2 call. This only remembers who was picked.
//
// Storage: localStorage, like swiftApp, because the rest of this app reads the acting user from `azure_id`, which
// authConfig.js now points at the picked person while viewing as them.
//   authenticated_azureid  the real signed-in user (written by authConfig.js)
//   imp_azureid            the person's azure id (missing when off)
//   imp_info               { target, target_kind, target_name, target_label, real_name, email, via, read_only, ... }
//   can_impersonate        '1' when the real user has anyone (staff) to view as; drives the avatar menu
import axios from 'axios';
import impersonationApi from '../api/impersonation.service.js';

const ID_KEY = 'imp_azureid';
const INFO_KEY = 'imp_info';
const CAN_KEY = 'can_impersonate';

export const ACT_AS_HEADER = 'X-GSD-Act-As';

const WEBAPIV2_ENDPOINT = process.env.VUE_APP_WEBAPIV2_ENDPOINT || '';

function read(key) {
  try { return localStorage.getItem(key) || ''; } catch (e) { return ''; }
}

function write(key, value) {
  try {
    if (value == null || value === '') localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (e) {
    /* storage blocked: impersonation simply won't survive a reload */
  }
}

/** The person being viewed as, or null. Banner fields come from imp_info (the server's last answer). */
export function getActAs() {
  const target = read(ID_KEY).trim();
  if (!target) return null;
  let info = null;
  try { info = JSON.parse(read(INFO_KEY) || 'null'); } catch (e) { info = null; }
  return { ...(info && info.target === target ? info : {}), target };
}

export function setActAs(value) {
  if (!value?.target) {
    write(ID_KEY, null);
    write(INFO_KEY, null);
    const real = read('authenticated_azureid');
    if (real) write('azure_id', real);
  } else {
    write(ID_KEY, value.target);
    write(INFO_KEY, JSON.stringify(value));
    write('azure_id', value.target);
  }
  // ImpersonationBanner.vue re-reads on this (swiftLoan has no app-wide "auth context ready" event).
  window.dispatchEvent(new Event('gs-imp-refresh'));
}

export const clearActAs = () => setActAs(null);

export const canImpersonate = () => read(CAN_KEY) === '1';

// swiftLoan is opened on one loan (?id=...), so start/stop reload the page you are on rather than going home: that
// resets every per-user cache at once (Vuex, component state, roles) and re-runs the header's checks as the new person.
const reloadHere = () => window.location.reload();

/** Server validates + audits; throws with its message on 403. Reloads the page on success. */
export async function startImpersonation(target) {
  const profile = await impersonationApi.start(target);
  const info = profile?.impersonation || {};
  setActAs({ ...info, target: info.target || target, target_name: info.target_name || profile?.display_name || '', email: profile?.email || '' });
  reloadHere();
}

export async function stopImpersonation({ reload = true } = {}) {
  if (getActAs()) await impersonationApi.stop().catch(() => {}); // audit only; never blocks stopping
  clearActAs();
  if (reload) reloadHere();
}

/**
 * Called by header.vue on every load, before it reads roles for the acting user.
 *  - viewing as someone: GSDesignMe/Get re-checks it (403 ends it) and refreshes the banner details. Returns the
 *    person's azure id, or '' when it was refused.
 *  - otherwise: works out whether the real user has any staff to view as. swiftLoan has no realtor experience, so a
 *    "Realtor partners"-only list (any LO with linked realtors) doesn't count.
 */
export async function refreshImpersonation() {
  const current = getActAs();
  if (current) {
    try {
      const me = await impersonationApi.me();
      if (me?.impersonation) setActAs({ ...me.impersonation, target: current.target, email: me.email || current.email || '' });
      write(CAN_KEY, '1');
    } catch (e) {
      if (e?.status === 403) {
        clearActAs();
        return '';
      }
      // Network / server error: keep going; every GSWebAPIv2 call is still checked server-side.
    }
    return getActAs()?.target || '';
  }

  try {
    const res = await impersonationApi.targets({ pageSize: 1 });
    write(CAN_KEY, (res?.groups || []).some(g => g.level !== 'realtors') ? '1' : null);
  } catch (e) {
    write(CAN_KEY, null);
  }
  return '';
}

// Every call to GSWebAPIv2 asks for the person being viewed as, as swiftMarketing's http.js does. Only that host gets
// the header (Swift API, ConsumerAPI, GSAI never see it); { skipActAs: true } in the request config opts out.
// A 403 impersonation_denied means the server no longer allows it (rights changed, or a forged value): end it cleanly.
export function installActAsInterceptors(client = axios) {
  client.interceptors.request.use(cfg => {
    if (cfg.skipActAs || !WEBAPIV2_ENDPOINT || !String(cfg.url || '').startsWith(WEBAPIV2_ENDPOINT)) return cfg;
    const actAs = getActAs();
    if (actAs) cfg.headers[ACT_AS_HEADER] = actAs.target;
    return cfg;
  });

  client.interceptors.response.use(undefined, error => {
    const res = error?.response;
    const errors = res?.data?.errors || res?.data?.Errors || [];
    if (res?.status === 403 && getActAs() && Array.isArray(errors) && errors.includes('impersonation_denied')) {
      clearActAs();
      reloadHere();
    }
    return Promise.reject(error);
  });
}
